import { UI_Paragraph } from 'ui/components/1-atoms/Paragraph/Paragraph';
import styles from './PreviewCard.module.scss';
import { UI_RichText } from 'ui/components/1-atoms/RichText/RichText';
import { UI_Picture, UI_PictureProps } from 'ui/components/1-atoms/Media/Picture/Picture';
import { UI_Link } from 'ui/components/1-atoms/Action/Link/Link';
import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import classNames from 'classnames';
import { LinkType } from 'helpers/types';
import { UI_Player } from 'ui/components/1-atoms/Media/Player/Player';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { useCanPlayVideo, useIsPendingVideo, useRequestPlay } from './ActiveVideoContext';

export type UI_PreviewCardImageSizeOptions = 'small' | 'medium' | 'large';

export interface UI_PreviewCardProps {
	id?: string | number;
	eagerPlay?: boolean;
	featuredImage?: UI_PictureProps;
	image?: UI_PictureProps;
	mobileImage?: UI_PictureProps;
	imageSize?: 'small' | 'medium' | 'large';
	imageCropping?: 'cover' | 'contain';
	tags?: {
		// todo maybe refactor to array
		first?: string;
		second?: string;
	};
	headline?: string;
	text?: string;
	link?: LinkType;
	video?: {
		mediaURL: string;
		posterURL: string;
	};
}

export const UI_PreviewCard: React.FC<UI_PreviewCardProps> = ({
	id,
	eagerPlay,
	featuredImage,
	image,
	mobileImage,
	imageSize,
	imageCropping = 'cover',
	tags,
	headline,
	text,
	link,
	video,
}) => {
	const { ref, inView, entry } = useInView({
		threshold: 0.2,
		rootMargin: '0px',
		triggerOnce: false,
		// v10 skips the first callback for an element that starts out of view, leaving `entry` undefined.
		// An explicit `false` restores v9 behaviour, so an eager card that starts off-screen still gets an `entry` and can pause.
		initialInView: false,
	});
	const effectiveInView = eagerPlay && !entry ? true : inView;
	const isPlaying = useCanPlayVideo(id, effectiveInView && !!video?.mediaURL);
	const isPending = useIsPendingVideo(id);
	const requestPlay = useRequestPlay(id);
	const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
	if (isPlaying && !hasPlayedOnce) setHasPlayedOnce(true);
	// Mount the real player early for a pending (next-up) card so it can start buffering ahead of its turn.
	const shouldMountPlayer = hasPlayedOnce || isPending;
	const preload = hasPlayedOnce ? 'auto' : isPending ? 'metadata' : 'none';
	return (
		<div className={styles.PreviewCard} ref={ref}>
			{link && (
				<UI_Link href={link.url} noUnderline={true}>
					{(image?.url || mobileImage?.url || video?.mediaURL) && (
						<div className={classNames(styles.PreviewCard_image, styles[`PreviewCard_image___${imageSize}`])}>
							{video?.mediaURL ? (
								<>
									<UI_Player
										cover
										url={video.mediaURL}
										poster={video.posterURL}
										preload={preload}
										isLight={!shouldMountPlayer}
										playing={isPlaying}
										muted={true}
										loop={true}
										controls={false}
									/>
									{!isPlaying && (
										<span
											role="button"
											tabIndex={0}
											aria-label={headline ? `Play video: ${headline}` : 'Play video'}
											className={styles.PreviewCard_playButton}
											onClick={(event) => {
												event.preventDefault();
												event.stopPropagation();
												requestPlay();
											}}
											onKeyDown={(event) => {
												if (event.key === 'Enter' || event.key === ' ') {
													event.preventDefault();
													event.stopPropagation();
													requestPlay();
												}
											}}
										>
											<span className={styles.PreviewCard_playIconContainer}>
												<span className={styles.PreviewCard_playIcon} />
											</span>
										</span>
									)}
								</>
							) : mobileImage?.url ? (
								<>
									<div className={styles.PreviewCard_mobileMedia}>
										<UI_Picture
											url={mobileImage.url}
											altText={mobileImage.altText ?? ''}
											imageCropping={imageCropping}
											focalPoint={mobileImage.focalPoint}
											sizes="100vw"
											fileType={mobileImage.fileType}
										/>
									</div>
									<div className={styles.PreviewCard_desktopMedia}>
										{featuredImage?.url ? (
											<UI_Picture
												url={featuredImage.url}
												altText={featuredImage.altText}
												imageCropping={imageCropping}
												focalPoint={featuredImage.focalPoint}
												sizes="(max-width: 768px) 100vw, 575px"
												fileType={featuredImage.fileType}
											/>
										) : (
											<UI_Picture
												url={image.url}
												altText={image.altText}
												imageCropping={imageCropping}
												focalPoint={image.focalPoint}
												sizes="(max-width: 768px) 100vw, 575px"
												fileType={image.fileType}
											/>
										)}
									</div>
								</>
							) : featuredImage?.url ? (
								<UI_Picture
									url={featuredImage.url}
									altText={featuredImage.altText}
									imageCropping={imageCropping}
									focalPoint={featuredImage.focalPoint}
									sizes="(max-width: 768px) 100vw, 575px"
									fileType={featuredImage.fileType}
								/>
							) : (
								<UI_Picture
									url={image.url}
									altText={image.altText}
									imageCropping={imageCropping}
									focalPoint={image.focalPoint}
									sizes="(max-width: 768px) 100vw, 575px"
									fileType={image.fileType}
								/>
							)}
						</div>
					)}
					<div className={styles.PreviewCard_info}>
						{tags && (
							<div className={styles.PreviewCard_tags}>
								{tags.first && <UI_Paragraph>{tags.first}</UI_Paragraph>}
								{tags.second && <UI_Paragraph className={styles.PreviewCard_secondTag}>{tags.second}</UI_Paragraph>}
							</div>
						)}
						{headline && (
							<UI_Heading headingLevel="h2" className={styles.PreviewCard_headline}>
								{headline}
							</UI_Heading>
						)}
						{text && <UI_RichText className={styles.PreviewCard_richText} content={text} />}
					</div>
				</UI_Link>
			)}
		</div>
	);
};
